// Command palette dialog shell — combobox ARIA pattern, scope pill, grouped
// listbox, live region, footer keyboard hints, match highlighting, and per-kind
// icons (nav rows render the navigationRegistry icon carried on the candidate,
// so palette glyphs always match the sidebar).
//
// Receives isOpen/onClose from PaletteProvider (via portal). When isOpen=false
// the component returns null so per-open state in the controller resets.
//
// Keyboard contract:
//   ↑ / ↓   — navigate candidates (wrapping)
//   ↵       — open active candidate + record recent + close
//   Tab     — toggle scope (contract ↔ global); always preventDefault
//   Esc     — clear query when non-empty; close when already empty
//   backdrop click — close
//
// Focus management: on mount we capture document.activeElement, rAF-focus the
// input, and lock body scroll. Cleanup restores focus + scroll.
//
// Scope announcements are IMMEDIATE (not debounced) — only result-count
// announcements use the 500 ms trailing debounce.
//
// When the controller reports recordsIncomplete (an org-wide record drain hit
// its cap), a visible honesty notice renders as the records section's footer,
// or standalone when no records section is present — including alongside the
// no-results state.

import {
  useId,
  useRef,
  useEffect,
  useCallback,
  useState,
} from "react";
import { useNavigate, useMatch } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  FileText,
  FilePen,
  Clipboard,
  Flag,
  Shield,
  Calendar,
  Receipt,
  Search,
} from "lucide-react";
import { usePaletteController } from "./usePaletteController";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import type { PaletteCandidate, RecordCandidate, InvoiceCandidate } from "./paletteTypes";
import type { MatchRange } from "./scoring";
import type {
  RecentEntry,
  ManagedContractEntry,
  DraftContractEntry,
  RecordEntry,
  InvoiceEntry,
} from "../../hooks/useNavRecents";
import type { ManagedContract } from "../../types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Constructs a persisted RecentEntry from a selectable PaletteCandidate.
 *
 * Palette candidate ids carry display prefixes (e.g. "mc:mc-1",
 * "record:item-7") to avoid cross-kind key collisions in the flat list.
 * RecentEntry.id must be the raw domain id so the storage dedupe key
 * (kind::id) round-trips correctly across visits and re-selections.
 *
 * Managed-contract, draft-contract, record, and invoice kinds are recorded;
 * nav, tab, and overflow candidates are excluded.
 */
function toRecentEntry(candidate: PaletteCandidate): RecentEntry | null {
  const now = new Date().toISOString();

  if (candidate.kind === "managed-contract") {
    // Strip "mc:" prefix — sources.ts uses "mc:${mc.id}" as the candidate id.
    // Recent-section candidates use "recent:mc:${e.id}" — handled by stripping everything before the last segment.
    const domainId = candidate.id.startsWith("recent:mc:")
      ? candidate.id.slice("recent:mc:".length)
      : candidate.id.startsWith("mc:")
        ? candidate.id.slice("mc:".length)
        : candidate.id;

    const entry: ManagedContractEntry = {
      kind: "managed-contract",
      id: domainId,
      title: candidate.title,
      url: candidate.url,
      visitedAt: now,
    };
    if (candidate.ctx !== undefined) entry.ctx = candidate.ctx;
    return entry;
  }

  if (candidate.kind === "draft-contract") {
    const domainId = candidate.id.startsWith("recent:draft:")
      ? candidate.id.slice("recent:draft:".length)
      : candidate.id.startsWith("draft:")
        ? candidate.id.slice("draft:".length)
        : candidate.id;

    const entry: DraftContractEntry = {
      kind: "draft-contract",
      id: domainId,
      title: candidate.title,
      url: candidate.url,
      visitedAt: now,
    };
    if (candidate.ctx !== undefined) entry.ctx = candidate.ctx;
    return entry;
  }

  if (candidate.kind === "record") {
    const rc = candidate as RecordCandidate;
    const domainId = candidate.id.startsWith("recent:record:")
      ? candidate.id.slice("recent:record:".length)
      : candidate.id.startsWith("record:")
        ? candidate.id.slice("record:".length)
        : candidate.id;

    const entry: RecordEntry = {
      kind: "record",
      id: domainId,
      title: candidate.title,
      url: candidate.url,
      visitedAt: now,
      recordType: rc.recordType,
      contractId: rc.contractId,
    };
    if (candidate.ctx !== undefined) entry.ctx = candidate.ctx;
    return entry;
  }

  if (candidate.kind === "invoice") {
    const ic = candidate as InvoiceCandidate;
    // Both prefixes must strip: recent-row candidates carry "recent:invoice:"
    // ids, and storing that prefixed form would break the kind::id storage
    // dedupe — every reselection would mint a new entry.
    const domainId = candidate.id.startsWith("recent:invoice:")
      ? candidate.id.slice("recent:invoice:".length)
      : candidate.id.startsWith("invoice:")
        ? candidate.id.slice("invoice:".length)
        : candidate.id;

    const entry: InvoiceEntry = {
      kind: "invoice",
      id: domainId,
      title: candidate.title,
      url: candidate.url,
      visitedAt: now,
      contractId: ic.contractId,
    };
    if (candidate.ctx !== undefined) entry.ctx = candidate.ctx;
    return entry;
  }

  return null;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

// ─── HighlightedText ─────────────────────────────────────────────────────────

/**
 * Renders `text` with inline <mark> highlights for each matched range.
 *
 * Ranges arrive in token-visit order from matchText (not position order).
 * We sort by start and merge overlapping ranges before walking the cursor so
 * a query like "details acceptance" against "Acceptance details record" does
 * not walk the cursor backwards.
 */
function HighlightedText({
  text,
  ranges,
}: {
  text: string;
  ranges?: MatchRange[];
}) {
  if (!ranges || ranges.length === 0) return <>{text}</>;

  // Sort by start position, then merge overlapping/adjacent ranges.
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: MatchRange[] = [];
  for (const r of sorted) {
    if (merged.length === 0) {
      merged.push({ ...r });
    } else {
      const last = merged[merged.length - 1];
      if (r.start <= last.end) {
        // Overlapping or adjacent — extend the previous range.
        last.end = Math.max(last.end, r.end);
      } else {
        merged.push({ ...r });
      }
    }
  }

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const range of merged) {
    if (range.start > cursor) {
      parts.push(text.slice(cursor, range.start));
    }
    parts.push(
      <mark
        key={range.start}
        className="bg-brand-50 text-brand-800 dark:bg-brand-900/30 dark:text-brand-200 rounded-sm"
      >
        {text.slice(range.start, range.end)}
      </mark>,
    );
    cursor = range.end;
  }
  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }
  return <>{parts}</>;
}

// ─── CandidateIcon ────────────────────────────────────────────────────────────

/**
 * Per-kind lucide icon at 15 px. Icons are always slate (text-slate-400/500)
 * regardless of whether the row is active — the plan locks this so violet is
 * never used on selection.
 */
function CandidateIcon({ candidate }: { candidate: PaletteCandidate }) {
  const cls = "w-[15px] h-[15px] shrink-0 text-slate-400 dark:text-slate-500";

  if (candidate.kind === "overflow") {
    return <Search className={cls} />;
  }
  if (candidate.kind === "managed-contract") {
    return <FileText className={cls} />;
  }
  if (candidate.kind === "draft-contract") {
    return <FilePen className={cls} />;
  }
  if (candidate.kind === "record") {
    const rc = candidate as RecordCandidate;
    if (rc.recordType === "deliverable") return <Clipboard className={cls} />;
    if (rc.recordType === "milestone") return <Flag className={cls} />;
    if (rc.recordType === "insurance" || rc.recordType === "sow") return <Shield className={cls} />;
    return <Clipboard className={cls} />;
  }
  if (candidate.kind === "invoice") {
    return <Receipt className={cls} />;
  }
  if (candidate.kind === "tab") {
    return <Calendar className={cls} />;
  }
  // nav — the candidate carries the registry's icon component, so the palette
  // always renders the same glyph as the sidebar for a given destination.
  if (candidate.kind === "nav") {
    const NavIcon = candidate.icon;
    return <NavIcon className={cls} />;
  }
  return <FileText className={cls} />;
}

// ─── RecordSkeletonRows ───────────────────────────────────────────────────────

/** Shimmer skeleton rows rendered inside "Records in this contract" while loading. */
function RecordSkeletonRows() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-4 py-2.5 animate-pulse"
          aria-hidden="true"
        >
          <div className="w-[15px] h-[15px] bg-slate-200 dark:bg-slate-700 rounded shrink-0" />
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
            <div className="h-2.5 bg-slate-100 dark:bg-slate-800 rounded w-1/3" />
          </div>
        </div>
      ))}
    </>
  );
}

// ─── IncompleteRecordsNotice ──────────────────────────────────────────────────

const INCOMPLETE_NOTICE = "Some records couldn't be searched — results may be incomplete.";

/** Honesty notice shown when the org-wide record drain was truncated (silent-cap guard). */
function IncompleteRecordsNotice() {
  return (
    <div className="px-4 py-1.5 text-caption text-slate-400 dark:text-slate-500">
      {INCOMPLETE_NOTICE}
    </div>
  );
}

// ─── CandidateRow ─────────────────────────────────────────────────────────────

function CandidateRow({
  candidate,
  isActive,
  id,
  onActivate,
  onSelect,
}: {
  candidate: PaletteCandidate;
  isActive: boolean;
  id: string;
  onActivate: () => void;
  onSelect: () => void;
}) {
  const activeClass = isActive
    ? "bg-brand-50 dark:bg-brand-900/20"
    : "hover:bg-slate-50 dark:hover:bg-slate-800/50";

  if (candidate.kind === "overflow") {
    return (
      <div
        id={id}
        role="option"
        aria-selected={isActive}
        className={`flex items-center gap-2 px-4 py-2 cursor-pointer text-body-sm text-brand-600 dark:text-brand-400 ${activeClass}`}
        onMouseDown={(e) => {
          e.preventDefault();
          onSelect();
        }}
        onMouseEnter={onActivate}
      >
        <CandidateIcon candidate={candidate} />
        <span>{candidate.label}</span>
      </div>
    );
  }

  return (
    <div
      id={id}
      role="option"
      aria-selected={isActive}
      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer ${activeClass}`}
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={onActivate}
    >
      <CandidateIcon candidate={candidate} />
      <div className="flex flex-col min-w-0">
        <span
          className={`text-body-sm truncate ${
            isActive ? "text-brand-700 dark:text-brand-300" : "text-slate-800 dark:text-slate-200"
          }`}
        >
          <HighlightedText text={candidate.title} ranges={candidate.titleRanges} />
        </span>
        {"ctx" in candidate && candidate.ctx && (
          <span className="font-mono text-caption text-slate-500 dark:text-slate-400 truncate">
            {candidate.ctx}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── CommandPalette ───────────────────────────────────────────────────────────

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const listboxId = useId();
  const scopeHintId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const savedFocusRef = useRef<Element | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reducedMotion = usePrefersReducedMotion();

  // Detect contract route: /manage/contracts/:id (excluding /new)
  const contractMatch = useMatch("/manage/contracts/:id");
  const contractId =
    contractMatch?.params.id && contractMatch.params.id !== "new"
      ? contractMatch.params.id
      : null;

  const {
    state,
    dispatch,
    sections,
    flatCandidates,
    activeCandidateUrl,
    isLoading,
    recordsIncomplete,
    recordRecent,
    clearRecents,
  } = usePaletteController({ isOpen, contractId });

  // Resolved contract title for scope announcements — pulled from RQ cache so
  // no extra fetch is needed. Falls back to "this contract" if not cached yet.
  const cachedContract = contractId
    ? (queryClient.getQueryData(["managed-contracts", contractId]) as ManagedContract | undefined)
    : undefined;
  const contractTitle = cachedContract?.title ?? "this contract";

  // Live region state — immediate for scope changes, 500 ms debounce for result counts.
  const [liveMessage, setLiveMessage] = useState("");
  const liveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const announceLive = useCallback((msg: string) => {
    if (liveTimerRef.current) clearTimeout(liveTimerRef.current);
    liveTimerRef.current = setTimeout(() => setLiveMessage(msg), 500);
  }, []);

  // On open: set initial scope to 'contract' when on a contract route, then
  // focus the input. Matches the prototype's useEffect(open, onContract) pattern
  // (command-palette.js lines 66-77: setScope(onContract ? 'contract' : 'global')).
  useEffect(() => {
    if (!isOpen) return;

    dispatch({ type: "OPEN", scope: contractId ? "contract" : "global" });

    savedFocusRef.current = document.activeElement;
    document.body.style.overflow = "hidden";

    // rAF to ensure the portal has mounted before focus
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = "";
      if (savedFocusRef.current instanceof HTMLElement) {
        savedFocusRef.current.focus();
      }
    };
  }, [isOpen, contractId, dispatch]);

  // Announce result count changes (debounced — fast typing should not spam the SR)
  useEffect(() => {
    if (!isOpen) return;
    const total = flatCandidates.length;
    if (state.query) {
      announceLive(
        total === 0 ? "No results" : `${total} result${total === 1 ? "" : "s"}`,
      );
    }
  }, [flatCandidates.length, state.query, isOpen, announceLive]);

  // Cleanup live timer on unmount
  useEffect(
    () => () => {
      if (liveTimerRef.current) clearTimeout(liveTimerRef.current);
    },
    [],
  );

  // Keep the active option visible inside the scrollable listbox.
  useEffect(() => {
    if (state.activeIndex < 0 || !listboxRef.current) return;
    const el = listboxRef.current.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!el) return;
    // scrollIntoViewIfNeeded is non-standard but widely supported; fall back
    // to manual scroll calculation for environments that don't have it.
    if ("scrollIntoViewIfNeeded" in el) {
      (el as HTMLElement & { scrollIntoViewIfNeeded: (arg: boolean) => void }).scrollIntoViewIfNeeded(false);
    } else {
      const lb = listboxRef.current;
      const top = lb.scrollTop;
      const bottom = top + lb.clientHeight;
      if (el.offsetTop < top) lb.scrollTop = el.offsetTop;
      else if (el.offsetTop + el.offsetHeight > bottom) {
        lb.scrollTop = el.offsetTop + el.offsetHeight - lb.clientHeight;
      }
    }
  }, [state.activeIndex]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          dispatch({ type: "MOVE_DOWN", totalItems: flatCandidates.length });
          break;
        case "ArrowUp":
          e.preventDefault();
          dispatch({ type: "MOVE_UP", totalItems: flatCandidates.length });
          break;
        case "Enter":
          e.preventDefault();
          if (activeCandidateUrl) {
            const active = flatCandidates[state.activeIndex];
            if (active) {
              const entry = toRecentEntry(active);
              if (entry) recordRecent(entry);
            }
            navigate(activeCandidateUrl);
            onClose();
          }
          break;
        case "Tab": {
          // Always preventDefault so Tab doesn't move browser focus out of
          // the palette, even when there is no contract context to toggle.
          e.preventDefault();
          if (contractId) {
            const nextScope = state.scope === "global" ? "contract" : "global";
            dispatch({ type: "TOGGLE_SCOPE", contractId });
            // Scope announcements are immediate (not debounced) so screen-reader
            // users hear the switch without waiting for the result-count delay.
            const announcement = nextScope === "contract"
              ? `Searching in ${contractTitle}`
              : "Searching everywhere";
            setLiveMessage(announcement);
          }
          break;
        }
        case "Escape":
          e.preventDefault();
          dispatch({ type: "ESC" });
          if (state.query === "") onClose();
          break;
      }
    },
    [
      activeCandidateUrl,
      flatCandidates,
      state.activeIndex,
      state.query,
      state.scope,
      contractId,
      contractTitle,
      dispatch,
      navigate,
      onClose,
      recordRecent,
    ],
  );

  const handleCandidateSelect = useCallback(
    (candidate: PaletteCandidate) => {
      const entry = toRecentEntry(candidate);
      if (entry) recordRecent(entry);
      navigate(candidate.url);
      onClose();
    },
    [navigate, onClose, recordRecent],
  );

  if (!isOpen) return null;

  const placeholder =
    state.scope === "contract" && contractId
      ? "Search this contract's records, tabs, or jump to…"
      : "Search contracts, drafts, or jump to…";

  const hasResults = sections.length > 0;
  const showNoResults = !!state.query.trim() && !hasResults && !isLoading;
  const showEmpty = !state.query.trim() && !hasResults;

  // Incomplete-search honesty: must render even when zero records matched —
  // a truncated drain with no visible matches is exactly the case where the
  // user would otherwise trust a wrong "No results".
  const showIncompleteNotice = recordsIncomplete && !!state.query.trim();
  const hasRecordsSection = sections.some((s) => s.kind === "records");

  // Scope pill copy matches the prototype (command-palette.js line 191):
  //   contract scope → "Searching in {contractTitle}"
  //   global  scope → "Searching everywhere"
  const scopePillText = state.scope === "contract"
    ? `Searching in ${contractTitle}`
    : "Searching everywhere";

  // Active candidate DOM id for aria-activedescendant
  const activeDescendant =
    state.activeIndex >= 0 && state.activeIndex < flatCandidates.length
      ? `${listboxId}-opt-${state.activeIndex}`
      : undefined;

  // Scope-aware empty-state copy: records are mentioned in both scopes —
  // global scope drains records org-wide, not just contract scope.
  const emptyText = state.scope === "contract"
    ? "Type to search this contract's records, tabs, and navigation"
    : "Type to search contracts, records, and navigation";

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/40 dark:bg-slate-950/60 z-40"
        aria-hidden="true"
        onMouseDown={() => onClose()}
      />

      {/* Dialog — overlay tier: shadow-soft-lg (light) / shadow-soft-lg-dark (dark).
          Centred by a flex wrapper (Modal.tsx pattern): transform-based centring
          (-translate-x-1/2) is overridden by the scale-in keyframe's own
          transform for the animation's duration, rendering the panel
          right-shifted before it snaps to centre on the final frame.
          pointer-events pass through the wrapper so backdrop clicks still
          reach the close handler underneath. */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] pointer-events-none">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          data-palette="true"
          className={`pointer-events-auto w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-soft-lg dark:shadow-soft-lg-dark ring-1 ring-slate-900/10 dark:ring-slate-700 flex flex-col overflow-hidden ${
            reducedMotion ? "" : "animate-scale-in"
          }`}
        >
          {/* ── Header: input (+ scope pill on contract routes) ── */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 dark:border-slate-800">
              <input
                ref={inputRef}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded="true"
                aria-controls={listboxId}
                aria-activedescendant={activeDescendant}
                aria-describedby={contractId ? scopeHintId : undefined}
                type="text"
                placeholder={placeholder}
                value={state.query}
                onChange={(e) =>
                  dispatch({ type: "SET_QUERY", query: e.target.value })
                }
                onKeyDown={handleInputKeyDown}
                className="flex-1 bg-transparent outline-hidden text-body text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 min-w-0"
                autoComplete="off"
                spellCheck="false"
              />

              {isLoading && (
                <span className="shrink-0 w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" aria-hidden="true" />
              )}
            </div>

            {/* Scope row — only on contract routes */}
            {contractId && (
              <div className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                <button
                  type="button"
                  tabIndex={-1}
                  aria-keyshortcuts="Tab"
                  className="shrink-0 text-caption px-2 py-0.5 rounded-md bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 font-medium"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const nextScope = state.scope === "global" ? "contract" : "global";
                    dispatch({ type: "TOGGLE_SCOPE", contractId });
                    setLiveMessage(
                      nextScope === "contract"
                        ? `Searching in ${contractTitle}`
                        : "Searching everywhere",
                    );
                    inputRef.current?.focus();
                  }}
                >
                  {scopePillText}
                </button>
                <p id={scopeHintId} className="text-caption text-slate-400 dark:text-slate-500">
                  Press <kbd className="font-mono">Tab</kbd> to search{" "}
                  {state.scope === "contract" ? "everywhere" : "this contract"}
                </p>
              </div>
            )}
          </div>

          {/* ── Listbox ── */}
          <div
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            aria-label="Results"
            className="flex flex-col overflow-y-auto max-h-[60vh] py-1"
          >
            {showEmpty && (
              <div className="px-4 py-8 text-center text-slate-400 dark:text-slate-500 text-body-sm">
                {emptyText}
              </div>
            )}

            {showNoResults && (
              <div className="px-4 py-8 text-center text-slate-400 dark:text-slate-500 text-body-sm">
                No results for &ldquo;{state.query}&rdquo;
              </div>
            )}

            {sections.map((section, sectionIdx) => {
              // Count candidates in all preceding sections to get the flat index
              // of this section's first candidate. Object-identity lookup is
              // avoided so mock data (separate array refs) still works correctly.
              const sectionStartIndex = sections
                .slice(0, sectionIdx)
                .reduce((sum, s) => sum + s.candidates.length, 0);

              // Records section: render skeleton rows while loading (before candidates arrive)
              const showRecordSkeleton = section.kind === "records" && isLoading && section.candidates.length === 0;

              return (
                <div key={section.kind} role="presentation">
                  {/* Section heading */}
                  <div
                    role="group"
                    aria-label={section.label}
                    className="flex items-center justify-between px-4 pt-3 pb-1"
                  >
                    <span className="text-overline text-slate-400 dark:text-slate-500 uppercase">
                      {section.label}
                    </span>
                    {section.kind === "recent" && (
                      <button
                        type="button"
                        className="text-caption text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          clearRecents();
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Skeleton rows for records section while loading */}
                  {showRecordSkeleton && <RecordSkeletonRows />}

                  {/* Candidates */}
                  {section.candidates.map((candidate, idx) => {
                    const flatIdx = sectionStartIndex + idx;
                    const isActive = flatIdx === state.activeIndex;
                    return (
                      <CandidateRow
                        key={candidate.id}
                        candidate={candidate}
                        isActive={isActive}
                        id={`${listboxId}-opt-${flatIdx}`}
                        onActivate={() =>
                          dispatch({ type: "SET_ACTIVE", index: flatIdx })
                        }
                        onSelect={() => handleCandidateSelect(candidate)}
                      />
                    );
                  })}

                  {/* Incompleteness notice as the records section's footer */}
                  {section.kind === "records" && showIncompleteNotice && <IncompleteRecordsNotice />}
                </div>
              );
            })}

            {/* Standalone fallback: no-results state, or a results list that
                happens to lack a records section (silent-cap guard still applies). */}
            {showIncompleteNotice && !hasRecordsSection && <IncompleteRecordsNotice />}
          </div>

          {/* ── Footer ── */}
          <div
            data-palette-footer="true"
            className="flex items-center gap-4 px-4 py-2 border-t border-slate-100 dark:border-slate-800 text-caption text-slate-400 dark:text-slate-500"
          >
            <span><kbd className="font-mono">↑↓</kbd> Navigate</span>
            <span><kbd className="font-mono">↵</kbd> Open</span>
            {contractId && <span><kbd className="font-mono">Tab</kbd> Scope</span>}
            <span><kbd className="font-mono">esc</kbd> Close</span>
          </div>
        </div>
      </div>

      {/* Live region — scope changes are written immediately; result counts are debounced */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {liveMessage}
      </div>
    </>
  );
}
